import { take, filter, finalize, switchMap, catchError, throwError, BehaviorSubject } from 'rxjs';

import { inject } from '@angular/core';
import { HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';

import { TokenStore } from '@core/services';

import { ToastService } from '@shared/components/toast';

import { ALLOW_REFRESH } from '../tokens/allow-refresh';

import { AuthFacade } from '@core/auth';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthFacade);
  const tokensStore = inject(TokenStore);
  const toastService = inject(ToastService);

  const addToken = (token: string) =>
    req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401 || !req.context.get(ALLOW_REFRESH)) {
        return throwError(() => error);
      }

      if (isRefreshing) {
        return refreshTokenSubject.pipe(
          filter((token): token is string => token !== null),
          take(1),
          switchMap((token) => next(addToken(token))),
        );
      }

      isRefreshing = true;
      refreshTokenSubject.next(null);

      return authService.refreshToken().pipe(
        switchMap((token) => {
          if (!token) {
            authService.logout();
            return throwError(() => error);
          }

          tokensStore.setAccessToken(token);
          refreshTokenSubject.next(token.key);

          return next(addToken(token.key));
        }),
        catchError((refreshError) => {
          authService.logout();
          toastService.error('Your session has timed-out. Please login again.');
          return throwError(() => refreshError);
        }),
        finalize(() => {
          isRefreshing = false;
        }),
      );
    }),
  );
};
